import {
  FilterDocumentDateFilter,
  FilterDocumentDurationFilter,
  FilterDocumentTagMatchMode,
} from '@griply/types';
import React, { useMemo } from 'react';
import { Check } from 'shared/assets/icons';
import { Option } from 'shared/components/ui/custom-form-select';
import { Icon } from 'shared/components/ui/icon';
import {
  ButtonVariants,
  PopupMenuButton,
  PopupMenuCheckbox,
  PopupMenuItemGroup,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuMultiSelect,
  PopupMenuSelect,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import {
  DateFormatOptions,
  initialDateFormat,
} from 'shared/types/date-format-options';
import {
  ActiveFilter,
  FilterDateRangeOption,
  FilterGroup as FilterGroupType,
  FilterOptionType,
  TagMatchModeOption,
} from 'shared/types/filter';
import { ID } from 'shared/types/id';
import { WeekDays } from 'shared/types/week-days';
import { processDurationFilterFromString } from 'shared/utils/filters';

import { FilterDateRange } from './filter-date-range';
import * as Styled from './filter-date-range.style';
import { FilterNumberRange } from './filter-number-range';
import { TagMatchModeInfoIcon } from './tag-match-mode-info-icon';
import * as TagModeStyled from './tag-match-mode-options.style';

export type FilterPillMenuItemProps = {
  id: ID;
  label: string;
  type?: FilterOptionType;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  tooltip?: string;
  selectOptions?: Option[];
  filters?: FilterGroupType[];
  placeholder?: string;
  minLabel?: string;
  maxLabel?: string;
  canReset?: boolean;
  recursiveSelection?: boolean;
  isDeadline?: boolean;
  buttonVariant?: ButtonVariants;
  dateFormat?: DateFormatOptions;
  hideUndefinedDateOptions?: boolean;
  hideFuturePresets?: boolean;
  infoUrl?: string;

  weekStartsOn?: WeekDays;
  enableSearch?: boolean;
  optionsAsButtons?: boolean;
  tagMatchMode?: FilterDocumentTagMatchMode;
  tagMatchModeOptions?: TagMatchModeOption[];
  onOption: (options: {
    id: ID;
    value?: string | string[];
    durationFilter?: FilterDocumentDurationFilter | null;
    dateFilter?: FilterDocumentDateFilter | null;
    tagMatchMode?: FilterDocumentTagMatchMode;
  }) => void;
  activeOptions: ActiveFilter[];
  level: number;
  disabled?: boolean;
};

export const FilterPillMenuItem: React.FC<FilterPillMenuItemProps> = ({
  id,
  label,
  type,
  icon,
  tooltip,
  selectOptions,
  filters,
  placeholder,
  minLabel,
  maxLabel,
  canReset,
  recursiveSelection,
  isDeadline,
  buttonVariant,
  dateFormat,
  weekStartsOn,
  hideUndefinedDateOptions,
  hideFuturePresets,
  infoUrl,
  onOption,
  activeOptions,
  level,
  enableSearch,
  optionsAsButtons,
  tagMatchMode,
  tagMatchModeOptions,
  disabled,
}) => {
  const active = useMemo(
    () =>
      !!activeOptions.find(
        (active) => active.id === id || active.value.includes(id),
      ),
    [activeOptions, id],
  );

  const enhancedSelectOptions = useMemo(
    () =>
      FilterOptionType.Select === type
        ? selectOptions?.map((option) => ({
            ...option,
            start: (
              <Styled.IconContainer>
                {activeOptions.find((active) => active.id === id)?.value[0] ===
                  option.value && <Icon icon={Check} />}
              </Styled.IconContainer>
            ),
          }))
        : undefined,
    [activeOptions, id, selectOptions, type],
  );

  if (filters?.length) {
    const _onChange = () => onOption({ id });

    return (
      // todo: adjust PopupMenuItemGroup to support a header instead of adding checkboxes and stuff in there
      <PopupMenuItemGroup
        id={id}
        label={label}
        onChange={_onChange}
        checked={active}
        children={filters.map((group) => (
          <FilterGroup
            group={group}
            onOption={onOption}
            activeOptions={activeOptions}
            level={level + 1}
          />
        ))}
      />
    );
  }

  if (type === FilterOptionType.Checkbox) {
    const _onChange = () => onOption({ id });
    return (
      <PopupMenuCheckbox
        id={id}
        label={label}
        onChange={_onChange}
        checked={active}
        level={level}
        compact
      />
    );
  }

  if (FilterOptionType.Select === type && enhancedSelectOptions?.length) {
    const _onChange = (id: ID, value?: string) => onOption({ id, value });

    return (
      <PopupMenuSelect
        id={id}
        label={label}
        onChange={_onChange}
        options={enhancedSelectOptions}
        placeholder={placeholder}
        selectedValue={
          activeOptions.find((active) => active.id === id)?.value[0]
        }
        icon={icon}
        level={level}
        canClear={canReset}
        compact
        isFilterStyle
      />
    );
  }

  if (
    FilterOptionType.MultiSelect === type &&
    (selectOptions?.length || tagMatchModeOptions?.length)
  ) {
    const _onChange = (id: ID, value?: string[]) => onOption({ id, value });
    const isNoTagMode = tagMatchMode === FilterDocumentTagMatchMode.NoTag;
    const noTagModeLabel = tagMatchModeOptions?.find(
      (option) => option.value === FilterDocumentTagMatchMode.NoTag,
    )?.label;
    const emptyLabel =
      isNoTagMode && noTagModeLabel ? noTagModeLabel : undefined;
    const mainOptions = tagMatchModeOptions?.length ? (
      <PopupMenuList hasBottomBorder>
        {tagMatchModeOptions.map((mode) => {
          const selected = tagMatchMode === mode.value;
          const onModeClick = () => onOption({ id, tagMatchMode: mode.value });
          return (
            <PopupMenuListItem key={mode.value}>
              <PopupMenuButton
                onClick={onModeClick}
                end={
                  selected ? (
                    <TagModeStyled.CheckIconContainer>
                      <Icon icon={Check} />
                    </TagModeStyled.CheckIconContainer>
                  ) : undefined
                }
              >
                <TagModeStyled.LabelWithInfo>
                  {mode.label}
                  <TagMatchModeInfoIcon description={mode.description} />
                </TagModeStyled.LabelWithInfo>
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>
    ) : undefined;

    return (
      <PopupMenuMultiSelect
        id={id}
        label={label}
        onChange={_onChange}
        options={selectOptions ?? []}
        placeholder={placeholder}
        icon={icon}
        level={level}
        selectedValue={activeOptions.find((active) => active.id === id)?.value}
        recursiveSelection={recursiveSelection}
        canClear={canReset}
        compact
        isFilterStyle
        enableSearch={enableSearch}
        optionsAsButtons={optionsAsButtons}
        mainOptions={mainOptions}
        emptyLabel={emptyLabel}
      />
    );
  }

  if (type === FilterOptionType.NumberRange) {
    const activeFilter = activeOptions.find((active) => active.id === id);
    const more = activeFilter?.value[0]
      ? processDurationFilterFromString(activeFilter?.value[0])
      : undefined;
    const less = activeFilter?.value[1]
      ? processDurationFilterFromString(activeFilter?.value[1])
      : undefined;

    const onFilterChange = (changes: FilterDocumentDurationFilter | null) =>
      onOption({ id, durationFilter: changes });

    return (
      <FilterNumberRange
        id={id}
        label={label}
        minLabel={minLabel ?? ''}
        maxLabel={maxLabel ?? ''}
        placeholder={placeholder ?? ''}
        lessValue={less?.value}
        moreValue={more?.value}
        lessType={less?.type}
        moreType={more?.type}
        onChange={onFilterChange}
        level={level}
        compact
        icon={icon}
        canClear={canReset}
      />
    );
  }

  if (type === FilterOptionType.DateRange) {
    const _onChange = (value: FilterDocumentDateFilter | null) =>
      onOption({
        id,
        dateFilter: value,
      });

    return (
      <FilterDateRange
        id={id}
        label={label}
        activeOptions={activeOptions}
        onChange={_onChange}
        dateFormat={dateFormat ?? initialDateFormat}
        weekStartsOn={weekStartsOn ?? WeekDays.Monday}
        level={level}
        compact
        icon={icon}
        canClear={canReset}
        isDeadline={isDeadline}
        hideUndefinedDateOptions={hideUndefinedDateOptions}
        hideFuturePresets={hideFuturePresets}
        infoUrl={infoUrl}
      />
    );
  }

  if (type === FilterOptionType.Button) {
    const onClick = () => onOption({ id });

    return (
      <PopupMenuButton
        onClick={onClick}
        variant={buttonVariant}
        disabled={disabled}
      >
        {label}
      </PopupMenuButton>
    );
  }

  return <PopupMenuListItem>{label}</PopupMenuListItem>;
};

export type FilterGroupProps = {
  group: FilterGroupType;
  hasBottomBorder?: boolean;
  onOption: (options: {
    id: ID;
    value?: string | string[];
    durationFilter?: FilterDocumentDurationFilter | null;
    dateFilter?: FilterDocumentDateFilter | null;
    tagMatchMode?: FilterDocumentTagMatchMode;
  }) => void;
  activeOptions: ActiveFilter[];
  level?: number;
};

export const FilterGroup: React.FC<FilterGroupProps> = ({
  group,
  hasBottomBorder,
  onOption,
  activeOptions,
  level,
}) => {
  return (
    <PopupMenuList hasBottomBorder={hasBottomBorder}>
      {!!group.title && <PopupMenuTitle title={group.title} isSubTitle />}
      {group.filters.map((filter) => (
        <PopupMenuListItem key={filter.id}>
          <FilterPillMenuItem
            id={filter.id}
            type={filter.type}
            label={filter.label}
            tooltip={filter.tooltip}
            filters={filter.children}
            canReset={filter.canReset}
            isDeadline={'isDeadline' in filter ? filter.isDeadline : undefined}
            buttonVariant={
              'buttonVariant' in filter ? filter.buttonVariant : undefined
            }
            icon={'icon' in filter ? filter.icon : undefined}
            selectOptions={
              'selectOptions' in filter ? filter.selectOptions : undefined
            }
            recursiveSelection={
              filter.type === FilterOptionType.MultiSelect
                ? filter.recursiveSelection
                : undefined
            }
            placeholder={
              'placeholder' in filter ? filter.placeholder : undefined
            }
            minLabel={
              filter.type === FilterOptionType.NumberRange
                ? filter.minLabel
                : undefined
            }
            maxLabel={
              filter.type === FilterOptionType.NumberRange
                ? filter.maxLabel
                : undefined
            }
            dateFormat={
              filter.type === FilterOptionType.DateRange
                ? (filter as FilterDateRangeOption).dateFormat
                : undefined
            }
            weekStartsOn={
              filter.type === FilterOptionType.DateRange
                ? (filter as FilterDateRangeOption).weekStartsOn
                : undefined
            }
            hideUndefinedDateOptions={
              filter.type === FilterOptionType.DateRange
                ? (filter as FilterDateRangeOption).hideUndefinedDateOptions
                : undefined
            }
            hideFuturePresets={
              filter.type === FilterOptionType.DateRange
                ? (filter as FilterDateRangeOption).hideFuturePresets
                : undefined
            }
            infoUrl={'infoUrl' in filter ? filter.infoUrl : undefined}
            enableSearch={
              'enableSearch' in filter ? filter.enableSearch : undefined
            }
            optionsAsButtons={
              'optionsAsButtons' in filter ? filter.optionsAsButtons : undefined
            }
            tagMatchMode={
              'tagMatchMode' in filter ? filter.tagMatchMode : undefined
            }
            tagMatchModeOptions={
              'tagMatchModeOptions' in filter
                ? filter.tagMatchModeOptions
                : undefined
            }
            onOption={onOption}
            activeOptions={activeOptions}
            level={level ?? 0}
            disabled={'disabled' in filter ? filter.disabled : undefined}
          />
        </PopupMenuListItem>
      ))}
    </PopupMenuList>
  );
};
